import React from 'react';
import { useAppContext } from '@front-activerse/common-components';

interface IStackedSourceThumbProps {
  imageUrl: string | null;
  /**
   * UI projection from the backend `badges` field — child source imageUrls
   * already deduplicated against the composite's main thumbnail. Rendered
   * verbatim with no client-side filtering.
   */
  badges: string[];
}

/**
 * Thumbnail for a merged activity (Figma MDS "Tracker Entity" variant
 * `type=Merged activity`, node 9086-2775).
 *
 * Layout:
 *   - Main image fills the frame — composite's own `imageUrl` (when backend
 *     provides one) or the brand's default activity thumbnail.
 *   - Per-source badges overlay the bottom-right corner, stacked with a small
 *     horizontal offset so multiple sources remain visible.
 */
const MAX_BADGES = 3;

const StackedSourceThumb: React.FC<IStackedSourceThumbProps> = ({ imageUrl, badges }) => {
  const {
    images: { trackerLoggedActivityThumb },
  } = useAppContext();

  const mainImage = imageUrl || trackerLoggedActivityThumb;
  const visibleBadges = badges.slice(0, MAX_BADGES);
  const overflow = badges.length - visibleBadges.length;

  return (
    <div className="merged-activity-thumb" data-testid="merged-activity-thumb" aria-hidden="true">
      <img src={mainImage} className="merged-activity-thumb-main" alt="" />

      {visibleBadges.length > 0 && (
        <div className="merged-activity-thumb-badges">
          {visibleBadges.map((url, index) => (
            <span
              key={`${url}-${index}`}
              className="merged-activity-thumb-badge"
              data-testid={`merged-activity-source-icon-${index}`}
            >
              <img src={url} alt="" width={18} height={18} />
            </span>
          ))}

          {overflow > 0 && (
            <span
              className="merged-activity-thumb-badge merged-activity-thumb-badge-overflow"
              data-testid="merged-activity-source-icon-overflow"
              title={`+${overflow}`}
            >
              +{overflow}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default StackedSourceThumb;
